import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';
const AuthContext = createContext(null);

// Token storage (in-memory, survives re-renders but not page refresh)
let _accessToken = null;
let _refreshToken = null;

// Try to restore from sessionStorage on load
try {
  _accessToken = sessionStorage.getItem('access_token');
  _refreshToken = sessionStorage.getItem('refresh_token');
} catch {}

function saveTokens(access, refresh) {
  _accessToken = access;
  _refreshToken = refresh;
  try {
    if (access) sessionStorage.setItem('access_token', access);
    else sessionStorage.removeItem('access_token');
    if (refresh) sessionStorage.setItem('refresh_token', refresh);
    else sessionStorage.removeItem('refresh_token');
  } catch {}
}

function clearTokens() {
  _accessToken = null;
  _refreshToken = null;
  try {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
  } catch {}
}

// Configure axios defaults - add auth header to every request
axios.interceptors.request.use((config) => {
  if (_accessToken) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${_accessToken}`;
  }
  // Toggle "Ver pacientes de toda a equipa" (só admin). Default off.
  try {
    config.headers = config.headers || {};
    config.headers['X-Team-View'] = localStorage.getItem('team_view') === 'true' ? 'true' : 'false';
  } catch {}
  config.withCredentials = true;
  return config;
});

// Auto-refresh on 401, handle 403 (pending/inactive)
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const slotDetail = error.response?.status === 409 ? error.response?.data?.detail : null;
    if (slotDetail?.code === 'slot_conflict' && !originalRequest._forced) {
      return new Promise((resolve, reject) => {
        window.dispatchEvent(new CustomEvent('slot-conflict', { detail: {
          message: slotDetail.message,
          onConfirm: () => {
            let body = {};
            try { body = JSON.parse(originalRequest.data || '{}'); } catch {}
            axios({ ...originalRequest, data: JSON.stringify({ ...body, force: true }), _forced: true }).then(resolve, reject);
          },
          onCancel: () => { error.response.data.detail = slotDetail.message; reject(error); },
        } }));
      });
    }
    if (error.response?.status === 403 && !originalRequest.url?.includes('/auth/')) {
      // Só termina a sessão quando o 403 é de estado de conta (pendente/inativa).
      // Um 403 de falta de permissão para um recurso específico NÃO deve fazer logout
      // (ex.: nutricionista a aceder a um endpoint admin-only dentro de uma página partilhada).
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string' && (detail.includes('Conta pendente') || detail.includes('Conta inativa'))) {
        clearTokens();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !originalRequest._retry && _refreshToken && !originalRequest.url?.includes('/auth/refresh') && !originalRequest.url?.includes('/auth/me')) {
      originalRequest._retry = true;
      try {
        const { data } = await axios.post(`${API}/api/auth/refresh`, { refresh_token: _refreshToken }, { withCredentials: true });
        if (data.access_token) {
          saveTokens(data.access_token, _refreshToken);
          originalRequest.headers['Authorization'] = `Bearer ${data.access_token}`;
          return axios(originalRequest);
        }
      } catch {}
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/me`);
      setUser(data);
    } catch {
      setUser(false);
      clearTokens();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
    if (data.access_token) saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, name, consentVersion = '') => {
    const { data } = await axios.post(`${API}/api/auth/register`, { email, password, name, consent_version: consentVersion });
    if (data.status === 'pendente') return data;
    if (data.access_token) saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const googleAuth = async (sessionId) => {
    const { data } = await axios.post(`${API}/api/auth/google`, { session_id: sessionId });
    if (data.status === 'pendente' || data.status === 'inativo') return data;
    if (data.access_token) saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/api/auth/logout`, {}); } catch {}
    clearTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, googleAuth, logout, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
